import { type ReactNode, useCallback } from "react";
import SingleTechFilter from "~/features/search/components/Filters/components/TechFilter/SingleTechFilter";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TechFilter } from "~/features/search/models/filterConfigModels";
import { Card } from "~/features/configurator/Linear/components/Card";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import { styled } from "~/common/mui";

const CategoryTitle = styled("h2")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  marginTop: 0,
  marginBottom: theme.spacing(3.75),
}));

const CardRow = styled("div")(({ theme }) => ({
  display: "flex",
  "& > * + *": {
    marginLeft: theme.spacing(4),
  },
}));

const TechCard = styled(Card)({
  padding: 20,
  display: "flex",
  "& > *": {
    flex: "1 0 0",
  },
  "& > * + *": {
    marginLeft: 32,
  },
});

const CategoryImage = styled("div")({
  width: 208,
  "& > *": {
    marginTop: "auto",
  },
});

interface Props {
  image?: ReactNode;
  items: TechFilter[];
  title: string;
}

export const DesktopTechFilterCategory = (props: Props) => {
  const { items, image } = props;

  const renderChildren = renderItems(items);

  return (
    <div className="scha__sa__tech-filter-category--desktop">
      <CategoryTitle>{props.title}</CategoryTitle>
      <CardRow>
        <TechCard disableHover={true}>
          {renderChildren}
        </TechCard>

        <CategoryImage>{image}</CategoryImage>
      </CardRow>
    </div>
  );
};

const ConnectedSingleTechFilter = ({ item }: { item: TechFilter }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const uid = item.uid;

  const min = useWatch({ control, name: `${uid}.min` });
  const max = useWatch({ control, name: `${uid}.max` });

  const onUpdateValues = useCallback(
    (type: "min" | "max", value: number | undefined) => {
      setValue(`${uid}.${type}`, value, { shouldDirty: true, shouldTouch: true });
    },
    [uid, setValue],
  );

  const assistantText = item.assistantLabel ? t(item.assistantLabel) : "";
  const title = (
    <>
      {item.assistantLabel && <span dangerouslySetInnerHTML={{ __html: assistantText }} />}
      {item.description && <LinearInfo info={item.description} />}
    </>
  );

  return (
    <SingleTechFilter
      key={uid}
      title={title}
      placeholderSuffix={item.unit}
      min={min}
      max={max}
      onUpdateValues={onUpdateValues}
      data-test-id={`${uid}`}
    />
  );
};

export const renderItems = (items: TechFilter[]) => {
  return (
    <>
      {items.map((item) => (
        <ConnectedSingleTechFilter key={item.uid} item={item} />
      ))}
    </>
  );
};
